import { useEffect, useState } from "react";
import {
  AppBar, Box, Toolbar, Typography, Container, Button,
  Dialog, DialogContent, DialogTitle, Divider, Avatar, Menu, MenuItem,
  IconButton, ListItemIcon,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { auth } from "./firebase";
import AppButton from "./AppButton";

const Header = ({ isSignedIn, userName, signInOpen, setSignInOpen }) => {
  const [loggedIn, setLoggedIn] = useState(isSignedIn);
  const [anchorEl, setAnchorEl] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    setLoggedIn(isSignedIn);
  }, [isSignedIn, userName]);

  const handleGoogleSignIn = async () => {
    const googleProvider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, googleProvider);
      setSignInOpen(false);
    } catch (err) {
      console.error("Sign-in error:", err?.code, err?.message);
    }
  };

  const handleSignOut = async () => {
    setAnchorEl(null);
    await signOut(auth);
  };

  const initials = userName
    ? userName.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "";

  return (
    <AppBar position="fixed" sx={{ background: "white", boxShadow: "0 1px 8px rgba(0,0,0,0.08)", zIndex: 1000 }}>
      <Container maxWidth="100%">
        <Toolbar disableGutters sx={{ justifyContent: "space-between" }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box component="img" src="/icons/ats.png" alt="ATS Verifier Logo" sx={{ height: { xs: 36, sm: 50 }, width: { xs: 36, sm: 50 }, mr: 1 }} />
            <Typography variant="h6" noWrap
              sx={{ fontWeight: 700, color: "black", fontSize: { xs: "0.95rem", sm: "1.25rem" } }}>
              ATS Verifier
            </Typography>
          </Box>

          {/* Right side */}
          {loggedIn && userName ? (
            <>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0 }}>
                <Avatar sx={{
                  bgcolor: "black", color: "white", width: 38, height: 38,
                  fontSize: "0.85rem", fontWeight: 700, cursor: "pointer",
                }}>
                  {initials}
                </Avatar>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                  sx: { mt: 1, minWidth: 180, borderRadius: 2, boxShadow: "0 4px 20px rgba(0,0,0,0.12)" },
                }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#111" }}>{userName}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>Signed in</Typography>
                </Box>
                <Divider />
                <MenuItem onClick={handleSignOut} sx={{ mt: 0.5, color: "#d32f2f", gap: 1 }}>
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <LogoutIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                  </ListItemIcon>
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          ) : (
            <AppButton onClick={() => setSignInOpen(true)}>
              Sign In
            </AppButton>
          )}
        </Toolbar>
      </Container>

      {/* Sign-in modal */}
      <Dialog open={signInOpen} onClose={() => setSignInOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 5, background: "linear-gradient(90deg, #111 0%, #444 100%)" }} />
        <DialogContent sx={{ px: 4, pt: 4, pb: 4, textAlign: "center" }}>
          <Box component="img" src="/icons/ats.svg" alt="logo" sx={{ width: 56, height: 56, mb: 1.5 }} />
          <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: "#111", mb: 0.5 }}>
            Welcome to ATS Verifier
          </Typography>
          <Typography variant="body2" sx={{ color: "#888", mb: 3, lineHeight: 1.6 }}>
            Upload your resume and instantly see how well<br />you match any job description.
          </Typography>
          <Divider sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ color: "#bbb", px: 1 }}>Continue with</Typography>
          </Divider>
          <Button variant="outlined" fullWidth onClick={handleGoogleSignIn}
            sx={{
              textTransform: "none", fontWeight: 600, fontSize: "0.95rem",
              borderColor: "#e0e0e0", color: "#3c4043", py: 1.3, borderRadius: 2, gap: 1.5,
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              "&:hover": { background: "#f8f9fa", borderColor: "#ccc", boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
            }}
          >
            <Box component="img"
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google" sx={{ width: 20, height: 20 }} />
            Sign in with Google
          </Button>
          <Typography variant="caption" sx={{ display: "block", color: "#bbb", mt: 3 }}>
            By signing in, you agree to our{" "}
            <Box component="span" onClick={() => setTermsOpen(true)}
              sx={{ color: "#555", textDecoration: "underline", cursor: "pointer", "&:hover": { color: "#111" } }}>
              terms of service
            </Box>.
          </Typography>
          <Button onClick={() => setSignInOpen(false)}
            sx={{ mt: 1, color: "#aaa", textTransform: "none", fontSize: "0.8rem" }}>
            <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} /> Maybe later
          </Button>
        </DialogContent>
      </Dialog>

      {/* Terms of Service modal */}
      <Dialog open={termsOpen} onClose={() => setTermsOpen(false)} fullScreen
        sx={{ zIndex: 1500 }} PaperProps={{ sx: { background: "#fafafa" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: "1px solid #eee", background: "white" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>Terms of Service</Typography>
          <IconButton onClick={() => setTermsOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ maxWidth: 720, mx: "auto", py: 4, px: { xs: 2, sm: 4 } }}>
          {[
            { title: "1. Data We Collect", body: "When you sign in, we collect your name and email address via Google Authentication. When you use the ATS Verifier tool, we process the resume and job description you submit solely to generate your match analysis." },
            { title: "2. How We Use Your Data", body: "Your data is used exclusively to provide the resume analysis service. We use your email to identify your account and track usage for service improvement. We do not sell, rent, or share your personal information with third parties for marketing purposes." },
            { title: "3. Resume & Job Description Content", body: "Resume and job description content submitted for analysis is processed in real time and is not permanently stored on our servers beyond the duration required to complete the analysis. We do not use your resume content to train AI models." },
            { title: "4. Third-Party Services", body: "We use Firebase (Google) for authentication and data storage, and third-party AI services for semantic analysis. These services operate under their own privacy policies. By using ATS Verifier, you acknowledge the involvement of these services." },
            { title: "5. Data Retention", body: "Your account data (email, usage records) is retained for as long as your account is active. You may request deletion of your data by contacting us at contact@webtrabs.com." },
            { title: "6. Changes to These Terms", body: "We reserve the right to update these terms at any time. Continued use of the service after changes constitutes acceptance of the updated terms." },
          ].map(({ title, body }) => (
            <Box key={title} sx={{ mb: 3.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#111", mb: 0.8 }}>{title}</Typography>
              <Typography variant="body2" sx={{ color: "#555", lineHeight: 1.8 }}>{body}</Typography>
            </Box>
          ))}
          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" sx={{ color: "#aaa" }}>
            Last updated: March 2026 · contact@webtrabs.com
          </Typography>
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default Header;
